import React from 'react';
import Utils from '../../utils/Utils';

type TagVariant = 'soft' | 'solid' | 'gradient';
type TagColor =
  | 'blue'
  | 'purple'
  | 'green'
  | 'pink'
  | 'red'
  | 'gray'
  | 'ai'
  | 'featured'
  | 'custom';

export interface UiTagProps extends React.HTMLAttributes<HTMLSpanElement> {
  label: string;
  variant?: TagVariant;
  color?: TagColor;
  icon?: React.ReactNode;
  className?: string;
}

const softStyles: Record<TagColor, string> = {
  blue: 'text-blue-700 bg-blue-50',
  purple: 'text-purple-700 bg-purple-50',
  green: 'text-success-700 bg-success-50',
  pink: 'text-pink-700 bg-pink-50',
  red: 'text-primary bg-primaryLight/30',
  gray: 'text-gray-700 bg-gray-100',
  ai: 'text-ai-purple bg-ai-purple-light-2',
  featured: 'text-brand-700 bg-brand-50',
  custom: '',
};

const solidStyles: Record<TagColor, string> = {
  blue: 'bg-blue-700 text-white',
  purple: 'bg-purple-700 text-white',
  green: 'bg-success-700 text-white',
  pink: 'bg-pink-700 text-white',
  red: 'bg-primary text-white',
  gray: 'bg-gray-700 text-white',
  ai: 'bg-ai-purple text-white',
  featured: 'bg-brand-600 text-white',
  custom: '',
};

const gradientStyles: Partial<Record<TagColor, string>> = {
  ai: 'bg-[linear-gradient(90deg,rgba(43,127,255,0.1)_0%,rgba(173,70,255,0.1)_100%)] text-gray-800',
  featured: 'bg-gradient-to-r from-brand-50 to-pink-50 text-brand-700',
};

export const UiTag: React.FC<UiTagProps> = ({
  label,
  variant = 'soft',
  color = 'blue',
  icon,
  className,
  ...props
}) => {
  const base = 'inline-flex items-center gap-2 px-3 py-[6px] rounded-full text-sm font-medium';

  let variantStyle = '';
  if (variant === 'soft') {
    variantStyle = softStyles[color];
  } else if (variant === 'solid') {
    variantStyle = solidStyles[color];
  } else if (variant === 'gradient') {
    variantStyle = gradientStyles[color] ?? softStyles[color];
  }

  return (
    <span className={Utils.cn(base, variantStyle, className)} {...props}>
      {icon && <span className="flex items-center">{icon}</span>}
      {label}
    </span>
  );
};
